// The data of a map showing a team or a match (?live=1; teams and matches, Ben 2026-10-30): the same
// three things a player's map gets (live/event-map.ts) — the competition + seasons for its pickers,
// the events, the match context — but the team's: its league's seasons, its shots or passes (its own
// or its opponents', a season; passes in one match when a match is on it), its league's results.

import { useMemo } from "react";
import type { ProtoEvent, ProtoEventCompetition, ProtoShotPassDetail, SeasonsResponse } from "@catbento/shared";
import { getModState, setModState } from "../modules/bridge.js";
import type { EventMapData } from "../modules/EventMapBody.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { loadSeasonMatches, toProtoFixtures } from "../live/results.js";
import { recipientsById, type EventKind } from "../live/event-map.js";
import { loadTeamMatchPasses, loadTeamSeasonPasses, loadTeamShots, type ShotSide } from "../live/team-shots.js";
import { useLiveLeagueChoices } from "./useLeagueTable.js";
import type { TeamRef } from "../live/team.js";

const NO_EVENTS: ProtoEvent[] = [];

/** `matchId`: a match's passes come on their own (one game, every attribute); without one, the
 *  season's (slim, Ben 2026-11-02). Shots are always the season's, narrowed to the match by the body. */
export function useLiveTeamEventMap(
  mod: HTMLElement,
  team: TeamRef,
  kind: EventKind,
  side: ShotSide,
  fixedSeason: string | null,
  matchId: number | null,
): EventMapData {
  const comp = team.competitionId;
  const league = useLiveLeagueChoices().find((c) => c.id === comp) ?? null;
  const seasonCodes = useLiveQuery(`team-shots:seasons:${comp}`, async () =>
    (await liveApi().get<SeasonsResponse>(`/seasons?competitionId=${comp}`)).seasons.map((s) => String(s.id)),
  );
  // A match fixes its season; else the stored one, the one the team was picked up in, the newest.
  const stored = getModState<string>(mod, "_eventMapSeason");
  const seasonCode = !seasonCodes
    ? null
    : (fixedSeason ?? [stored, team.season].find((s): s is string => !!s && seasonCodes.includes(s)) ?? seasonCodes[0] ?? null);
  const shots = useLiveQuery(kind !== "shots" || seasonCode === null ? null : `team-shots:${team.id}:${comp}:${seasonCode}:${side}`, () =>
    loadTeamShots(liveApi(), team.id, comp, seasonCode as string, side),
  );
  const passesKey =
    kind !== "passes" ? null : matchId !== null ? `team-passes:${team.id}:${matchId}` : seasonCode === null ? null : `team-passes:${team.id}:${comp}:${seasonCode}:${side}`;
  const passes = useLiveQuery(passesKey, () =>
    matchId !== null ? loadTeamMatchPasses(liveApi(), team.id, matchId) : loadTeamSeasonPasses(liveApi(), team.id, comp, seasonCode as string, side),
  );
  const matches = useLiveQuery(seasonCode === null ? null : `matches:season:${comp}:${seasonCode}`, () =>
    loadSeasonMatches(liveApi(), comp, seasonCode as string),
  );
  if (seasonCode !== null) {
    setModState(mod, "_eventMapCompetitionId", String(comp));
    setModState(mod, "_eventMapSeason", seasonCode);
  }

  return useMemo<EventMapData>(() => {
    const none: EventMapData = { spd: null, state: null, ctx: { matchResults: {}, matchGoals: {}, playersById: new Map() } };
    const events = kind === "shots" ? shots : passes;
    if (!seasonCodes || seasonCode === null || !events || !matches) return none;
    const theSeason = kind === "shots" ? { shots: events, passes: NO_EVENTS } : { shots: NO_EVENTS, passes: events };
    const competition: ProtoEventCompetition = {
      name: league?.label ?? "League",
      seasons: Object.fromEntries(seasonCodes.map((code) => [code, code === seasonCode ? theSeason : { shots: [], passes: [] }])),
    };
    const spd: ProtoShotPassDetail = {
      playerId: 0,
      playerName: team.name,
      competitions: [{ id: comp, name: competition.name, country_name: null }],
      byCompetition: { [String(comp)]: competition },
    };
    return {
      spd,
      state: { compId: String(comp), comp: competition, seasonCodes, seasonCode, season: competition.seasons[seasonCode]! },
      // No Game State chip for a team yet (it reads a player's goal timeline).
      ctx: {
        matchResults: { [String(comp)]: toProtoFixtures(matches) },
        matchGoals: {},
        playersById: kind === "passes" ? recipientsById(events) : new Map(),
      },
    };
  }, [kind, seasonCodes, seasonCode, shots, passes, matches, league, team.name, comp]);
}
